import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CustodyAdjustmentIcon } from '../../../../assets/images/icons/custody_adjustment.svg';
import { ReactComponent as IntegrationResyncIcon } from '../../../../assets/images/icons/integration_resync.svg';
import { Refresh } from '@material-ui/icons';
import MaterialTable from '@material-table/core';
import dayjs from 'dayjs';
import { getPortfolioMappings, startResync } from '../../../../services/integrations';
import { IntegrationsAPIStatusEnum, TransactionsSyncProviderEnum } from '../../../../shared/integrations';
import { ConfirmationModal } from '../../../../shared';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const PortfolioIntegrations = ({ setMessage, data: portfolioData, setIntegrationsCount }) => {
  const { t: translate } = useTranslation();

  const initialModalState = {
    title: null,
    text: null,
    action: () => {},
    single: false,
    loading: false
  };

  const [tableLoading, setTableLoading] = useState(true);
  const [modalState, setModalState] = useState(initialModalState);
  const [integrationsData, setIntegrationsData] = useState([]);
  const [noDataMessage, setNoDataMessage] = useState('');

  const allowedProviders = ['B3'];

  const disabledColor = '#ABAEB8';
  const enabledColor = '#333333';

  const fetchIntegrationsData = useCallback(async () => {
    const { data } = await getPortfolioMappings(portfolioData.id);

    return data.map((mapping) => ({ ...mapping, migratedAt: mapping.integration?.providerMetadata?.migratedAt }));
  }, [portfolioData.id]);

  useEffect( () => {
    const loadIntegrations = async () => {
      try {
        setTableLoading(true);
        const data = await fetchIntegrationsData();

        setNoDataMessage(translate('no records to display'));
        setIntegrationsCount(data.length);
        setIntegrationsData(data);
      } catch (err) {
        console.error(err);
        setMessage(translate('it was not possible to get table data'));
        setNoDataMessage(translate('it was not possible to get table data'));
      }
      setTableLoading(false);
    };

    loadIntegrations();
  }, [fetchIntegrationsData, setMessage, setIntegrationsCount, translate]);

  const isAllowedToResyncAndAdjustment = (rowData) => {
    const isAllowedProvider = allowedProviders.includes(rowData.provider);

    return isAllowedProvider && rowData.status === IntegrationsAPIStatusEnum.ACTIVE && !rowData.migratedAt;
  };

  const updateTable = async () => {
    try {
      setTableLoading(true);
      const data = await fetchIntegrationsData();
      setIntegrationsData(data);
    } catch (err) {
      console.error(err);
      setMessage(translate('it was not possible to get table data'));
    } finally {
      setTableLoading(false);
    }
  };

  const triggerResync = async (rowData, custodyAdjustment = false) => {
    const actionMessage = custodyAdjustment ? 'custody adjustment': 'resync';

    try {
      setModalState((prevState) =>({ ...prevState, loading: true }));
      const providerId = TransactionsSyncProviderEnum[rowData.provider];
      const { data: response } = await startResync(providerId, portfolioData.id, custodyAdjustment);

      setModalState({
        ...initialModalState,
        title: translate(`${actionMessage} started for @providerName`, { providerName: rowData.provider }),
        text: <><b style={{ marginRight: '0.25rem' }}>{translate('process id')}:</b> {response.processStatusId}</>,
        single: true,
        loading: false,
        action: async () => {
          setModalState(initialModalState);
          await updateTable();
        }
      });
    } catch (err) {
      const response = err.response || err;

      setModalState(initialModalState);
      await updateTable();

      if (response.status === 409) {
        setMessage(translate('there is already a process running for this portfolio'));
      } else {
        setMessage(translate(`it was not possible to apply the ${actionMessage}`));
      }
    }
  };

  return (
    <div style={{ maxWidth: '100%' }}>
      <MaterialTable
        options={{
          draggable: false,
          paging: false,
          search: false,
          showTitle: false,
          sorting: false,
          headerStyle: { fontWeight: 'bold' },
          actionsColumnIndex: -1
        }}
        columns={[
          { title: translate('platform'), field: 'provider' },
          { title: translate('last update'), render: (rowData) => dayjs(rowData.syncedAt).format('DD/MM/YY[ às ]HH:mm') },
          { title: translate('state'), field: 'status' }
        ]}
        data={integrationsData}
        actions={[
          {
            icon: Refresh,
            tooltip: translate('refresh table'),
            isFreeAction: true,
            onClick: updateTable
          },
          (rowData) => {
            const disabled = !isAllowedToResyncAndAdjustment(rowData);

            return {
              icon: () => <CustodyAdjustmentIcon style={{ fill: disabled? disabledColor: enabledColor }} />,
              tooltip: translate('apply custody adjustment'),
              isFreeAction: false,
              disabled: disabled,
              onClick: async ()=> {
                if (!disabled) {
                  setModalState({
                    ...initialModalState,
                    title: translate('confirm start custody adjustment for @providerName', { providerName: rowData.provider }),
                    text: <>
                      <p><b style={{ marginRight: '0.25rem' }}>Portfolio ID:</b> {portfolioData.id}</p>
                      <p style={{ display: 'flex' }}>
                        <InfoOutlinedIcon style={{ marginRight: '5px' }}/>
                        {translate('the custody adjustment will also remove and reinsert all trades')}
                      </p>
                    </>,
                    action: () => triggerResync(rowData, true)
                  });
                }
              }
            };
          },
          (rowData) => {
            const disabled = !isAllowedToResyncAndAdjustment(rowData);

            return {
              icon: () => <IntegrationResyncIcon style={{ fill: disabled? disabledColor: enabledColor }} />,
              tooltip: translate('resync integration'),
              isFreeAction: false,
              disabled: disabled,
              onClick: async ()=> {
                if (!disabled) {
                  setModalState({
                    ...initialModalState,
                    title: translate('confirm start resync for @providerName', { providerName: rowData.provider }),
                    text: <>
                      <p><b style={{ marginRight: '0.25rem' }}>Portfolio ID:</b> {portfolioData.id}</p>
                      <p style={{ display: 'flex' }}>
                        <InfoOutlinedIcon style={{ marginRight: '5px' }}/>
                        {translate('the resync will remove and reinsert all trades')}
                      </p>
                    </>,
                    action: () => triggerResync(rowData, false)
                  });
                }
              }
            };
          }
        ]}
        localization={{
          body: {
            emptyDataSourceMessage: noDataMessage
          },
          header: {
            actions: translate('actions')
          }
        }}
        isLoading={tableLoading}
      />
      {modalState?.title &&
      <ConfirmationModal
        openModal={!!modalState?.title}
        closeModal={() => setModalState(initialModalState)}
        name={<b>{modalState.title}</b>}
        additionalInfo={modalState.text}
        confirm={modalState.action}
        disabled={modalState.loading}
        single={modalState.single}
        cancel='contained'
        loading={modalState.loading}
      />}
    </div>
  );
};

export default PortfolioIntegrations;
